import React, { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { useAuth } from "@workos-inc/authkit-react";
import api from "../http";

export interface Assistant {
  id: number;
  name: string;
  photo_url?: string | null;
  sharepoint_root_folder?: string | null;
}

interface AssistantAvatarContextType {
  assistants: Assistant[];
  currentAssistant: Assistant | null;
  assistantAvatarUrl: string;
  isLoading: boolean;
  isLoadingPhoto: boolean;
  createAssistant: (name: string) => Promise<Assistant | undefined>;
  updateAssistantName: (assistantId: number, name: string) => Promise<void>;
  updateAssistant: (assistantId: number, updates: { name?: string; sharepoint_root_folder?: string }) => Promise<void>;
  deleteAssistant: (assistantId: number) => Promise<void>;
  setCurrentAssistant: (assistantId: number | null) => Promise<void>;
  uploadAssistantPhoto: (assistantId: number, file: File) => Promise<void>;
  refreshAssistants: () => Promise<void>;
}

const AssistantAvatarContext = createContext<AssistantAvatarContextType | undefined>(undefined);

export const useAssistantAvatar = () => {
  const context = useContext(AssistantAvatarContext);
  if (context === undefined) {
    throw new Error('useAssistantAvatar must be used within an AssistantAvatarProvider');
  }
  return context;
};

interface AssistantAvatarProviderProps {
  children: ReactNode;
}

export const AssistantAvatarProvider: React.FC<AssistantAvatarProviderProps> = ({
  children,
}) => {
  const [assistants, setAssistants] = useState<Assistant[]>([]);
  const [currentAssistantId, setCurrentAssistantId] = useState<number | null>(null);
  const [assistantAvatarUrl, setAssistantAvatarUrl] = useState<string>("");
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingPhoto, setIsLoadingPhoto] = useState(false);
  const { getAccessToken, user } = useAuth();

  const currentAssistant =
    assistants.find((a) => a.id === currentAssistantId) ?? null;

  const syncAvatarUrl = (assistant: Assistant | null) => {
    setAssistantAvatarUrl(assistant?.photo_url || "");
  };

  const loadAssistants = async () => {
    if (!user) return;
    setIsLoadingPhoto(true);
    try {
      const token = await getAccessToken();
      const response = await api.getAssistants(token);
      const list: Assistant[] = response.assistants || [];
      setAssistants(list);

      // Load current user to determine current assistant selection
      const userData = await api.getCurrentUser(token);
      let currentId = userData.current_assistant_id ?? null;
      if (currentId === null && list.length > 0) {
        currentId = list[0].id;
      }
      setCurrentAssistantId(currentId);
      const current = list.find((a) => a.id === currentId) ?? null;
      syncAvatarUrl(current);
    } catch (error) {
      console.error("Failed to load assistants:", error);
      setAssistants([]);
      setCurrentAssistantId(null);
      syncAvatarUrl(null);
    } finally {
      setIsLoadingPhoto(false);
    }
  };

  const createAssistant = async (name: string): Promise<Assistant | undefined> => {
    setIsLoading(true);
    try {
      const token = await getAccessToken();
      const response = await api.createAssistant(name, token);
      const assistant: Assistant = response.assistant;
      setAssistants((prev) => [...prev, assistant]);
      return assistant;
    } finally {
      setIsLoading(false);
    }
  };

  const updateAssistantName = async (assistantId: number, name: string) => {
    await updateAssistant(assistantId, { name });
  };

  const updateAssistant = async (
    assistantId: number,
    updates: { name?: string; sharepoint_root_folder?: string }
  ) => {
    setIsLoading(true);
    try {
      const token = await getAccessToken();
      const response = await api.updateAssistant(assistantId, updates, token);
      const updated: Assistant = response.assistant;
      setAssistants((prev) =>
        prev.map((a) => (a.id === assistantId ? updated : a))
      );
      if (currentAssistantId === assistantId) {
        syncAvatarUrl(updated);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const deleteAssistant = async (assistantId: number) => {
    setIsLoading(true);
    try {
      const token = await getAccessToken();
      await api.deleteAssistant(assistantId, token);
      setAssistants((prev) => prev.filter((a) => a.id !== assistantId));
      if (currentAssistantId === assistantId) {
        setCurrentAssistantId(null);
        syncAvatarUrl(null);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const setCurrentAssistant = async (assistantId: number | null) => {
    setIsLoading(true);
    try {
      const token = await getAccessToken();
      await api.setCurrentAssistant(assistantId, token);
      setCurrentAssistantId(assistantId);
      const current = assistants.find((a) => a.id === assistantId) ?? null;
      syncAvatarUrl(current);
    } finally {
      setIsLoading(false);
    }
  };

  const uploadAssistantPhoto = async (assistantId: number, file: File) => {
    setIsLoading(true);
    try {
      const token = await getAccessToken();
      const response = await api.uploadAssistantPhotoForId(
        assistantId,
        file,
        token
      );
      const updated: Assistant = response.assistant;
      setAssistants((prev) =>
        prev.map((a) => (a.id === assistantId ? updated : a))
      );
      if (currentAssistantId === assistantId) {
        syncAvatarUrl(updated);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const refreshAssistants = async () => {
    await loadAssistants();
  };

  useEffect(() => {
    if (user) {
      loadAssistants();
    }
  }, [user]);

  useEffect(() => {
    syncAvatarUrl(currentAssistant);
  }, [currentAssistantId, assistants]);

  const value: AssistantAvatarContextType = {
    assistants,
    currentAssistant,
    assistantAvatarUrl,
    isLoading,
    isLoadingPhoto,
    createAssistant,
    updateAssistantName,
    updateAssistant,
    deleteAssistant,
    setCurrentAssistant,
    uploadAssistantPhoto,
    refreshAssistants,
  };

  return (
    <AssistantAvatarContext.Provider value={value}>
      {children}
    </AssistantAvatarContext.Provider>
  );
};